import { compareSemver } from "./semver.js";

// connectCommands.js — pure helpers behind the Connect surface: platform
// detection, installer selection, and the per-platform install command.
//
// Command syntax mirrors the agent's real install contract (see
// agent/resources/WINDOWS_INSTALLER_GUIDE.md and the macOS pkg postinstall):
// the Windows installers take the token as the PROVISIONING_JWT property;
// the macOS pkg uses the staged /var/tmp/origin-provisioning-jwt file because
// macOS installer does not reliably pass environment variables to package
// scripts. Don't refactor these strings from memory - they are the contract,
// not an example.

export const TOKEN_PLACEHOLDER = "<provisioning-token>";

export function detectInstallPlatform(navLike = typeof navigator !== "undefined" ? navigator : null) {
    const s = `${navLike?.userAgentData?.platform || ""} ${navLike?.platform || ""} ${navLike?.userAgent || ""}`;
    if (/mac/i.test(s)) return "PLATFORM_MACOS";
    if (/win/i.test(s)) return "PLATFORM_WINDOWS";
    if (/linux|x11/i.test(s)) return "PLATFORM_LINUX";
    return null;
}

// The bundle EXE self-elevates via UAC, so it's the foolproof single-machine
// path; the MSI is the fleet-tool variant (msiexec under SYSTEM). Prefer the
// EXE when both exist. x64 first among arches — the common case; the
// Installers page carries the full matrix for everything else.
const FORMAT_PREFERENCE = {
    PLATFORM_WINDOWS: ["INSTALLER_FORMAT_EXE", "INSTALLER_FORMAT_MSI"],
    PLATFORM_MACOS: ["INSTALLER_FORMAT_PKG"],
};

const ARCH_PREFERENCE = [
    "PROCESSOR_ARCHITECTURE_X64",
    "PROCESSOR_ARCHITECTURE_ARM64",
    "PROCESSOR_ARCHITECTURE_X86",
];

function archRank(arch) {
    const i = ARCH_PREFERENCE.indexOf(arch);
    return i === -1 ? ARCH_PREFERENCE.length : i;
}

// Ordered platforms present in the (already latest-per-environment) rows,
// Windows/macOS/Linux order so the chips render stably.
const PLATFORM_ORDER = ["PLATFORM_WINDOWS", "PLATFORM_MACOS", "PLATFORM_LINUX"];
export function installerPlatforms(rows) {
    const present = new Set(rows.map((r) => r.os));
    return [
        ...PLATFORM_ORDER.filter((p) => present.has(p)),
        ...Array.from(present).filter((p) => !PLATFORM_ORDER.includes(p)).sort(),
    ];
}

// The one artifact the Connect surface recommends for a platform. `rows`
// are expected pre-reduced to the newest build per environment.
export function pickInstaller(rows, platform, requestedFormat = null) {
    const forPlatform = rows
        .filter(
            (r) =>
                r.os === platform &&
                // A manual-test build is never what an install command should
                // name, whatever format was asked for.
                !r.manualTesting &&
                (!requestedFormat || r.installerFormat === requestedFormat),
        )
        .sort((a, b) => archRank(a.architecture) - archRank(b.architecture) || compareSemver(b.version, a.version));
    if (!forPlatform.length) return null;
    if (requestedFormat) return forPlatform[0];
    for (const format of FORMAT_PREFERENCE[platform] || []) {
        const match = forPlatform.find((r) => r.installerFormat === format);
        if (match) return match;
    }
    return forPlatform[0];
}

const ARTIFACT_OS = {
    PLATFORM_WINDOWS: "windows",
    PLATFORM_MACOS: "macos",
};

const ARTIFACT_ARCH = {
    PROCESSOR_ARCHITECTURE_X64: "x64",
    PROCESSOR_ARCHITECTURE_ARM64: "arm64",
    PROCESSOR_ARCHITECTURE_X86: "x86",
};

const FORMAT_EXTENSION = {
    INSTALLER_FORMAT_MSI: "msi",
    INSTALLER_FORMAT_EXE: "exe",
    INSTALLER_FORMAT_PKG: "pkg",
};

// Keep this in sync with installer_artifact_filename in trigger-release.sh.
// The macOS package is universal2 and is registered under both architecture
// keys, but its downloaded filename deliberately has no architecture suffix.
export function installerArtifactFilename({ version, platform, architecture, format }) {
    const os = ARTIFACT_OS[platform];
    const extension = FORMAT_EXTENSION[format];
    if (!version || !os || !extension) return null;
    if (platform === "PLATFORM_MACOS") return `origin-installer-${version}-${os}.${extension}`;
    const arch = ARTIFACT_ARCH[architecture];
    if (!arch) return null;
    return `origin-installer-${version}-${os}-${arch}.${extension}`;
}

function shellQuote(value) {
    return `'${String(value).replaceAll("'", "'\\''")}'`;
}

// The copyable install command. If installer metadata is unavailable, retain
// the extension-only placeholder rather than producing a command for a file
// that cannot be downloaded. No command for Linux: no packaged install
// contract exists to document.
export function installCommandFor({ platform, format, version, architecture, jwt }) {
    const token = jwt || TOKEN_PLACEHOLDER;
    const extension = FORMAT_EXTENSION[format];
    const packageName = installerArtifactFilename({ version, platform, architecture, format });
    const installer = packageName || `<installer>${extension ? `.${extension}` : ""}`;
    if (platform === "PLATFORM_WINDOWS") {
        // Provisioning JWTs are compact JWS values: three base64url segments
        // separated by dots. That alphabet excludes quotes and cmd.exe/
        // PowerShell metacharacters, so the installer's documented
        // PROVISIONING_JWT="..." form needs no additional Windows escaping.
        // These strings are copy-pasted by the operator; the dashboard never
        // executes them.
        if (format === "INSTALLER_FORMAT_MSI") {
            return `msiexec /i ${installer} /quiet /norestart PROVISIONING_JWT="${token}"`;
        }
        return `${installer} /quiet /norestart PROVISIONING_JWT="${token}"`;
    }
    if (platform === "PLATFORM_MACOS") {
        return `umask 077 && echo ${shellQuote(token)} > /var/tmp/origin-provisioning-jwt && sudo installer -pkg ${installer} -target /`;
    }
    return null;
}

// Cheap shape check for the paste field — three dot-separated base64url
// segments. Not validation (the agent validates properly); just enough to
// know the paste is worth splicing into the command.
export function looksLikeJwt(s) {
    return /^[\w-]+\.[\w-]+\.[\w-]+$/.test(String(s || "").trim());
}
